import { useReceivedRequestsPaginated } from "../api/queries/useReceivedRequests";
import { useOrderedRequestsPaginated } from "../api/queries/useOrderedRequests";
import { useReceivedContractsPaginated } from "../api/queries/useReceivedContracts";
import { useOrderedContractsPaginated } from "../api/queries/useOrderedContracts";
import { ServiceCategoryEnum } from "../types/service.type";
import { useProfileData } from "../../../hooks/useProfileData";
import { useSearchParams } from "react-router-dom";

interface UseServiceRequestsCountsResult {
  newCount: number;
  contractCount: number;
  historyCount: number;
  isLoading: boolean;
}

export const useServiceRequestsCounts = (): UseServiceRequestsCountsResult => {
  const { data: userData, isLoading: isProfileLoading } = useProfileData();
  const { id } = userData || {};
  const [searchParams] = useSearchParams();
  const requestType = searchParams.get("request-type") || "received";

  // Get received requests counts
  const { data: receivedNewRequests, isLoading: isReceivedNewLoading } = useReceivedRequestsPaginated({
    category: "new" as ServiceCategoryEnum,
    limit: 1,
    enabled: requestType === "received",
  });

  const { data: receivedHistoryRequests, isLoading: isReceivedHistoryLoading } = useReceivedRequestsPaginated({
    category: "history" as ServiceCategoryEnum,
    limit: 1,
    enabled: requestType === "received",
  });

  const { data: receivedContracts, isLoading: isReceivedContractsLoading } = useReceivedContractsPaginated({
    category: "contract" as ServiceCategoryEnum,
    limit: 1,
    enabled: requestType === "received" && !!id,
    userId: String(id),
  });

  // Get ordered requests counts
  const { data: orderedNewRequests, isLoading: isOrderedNewLoading } = useOrderedRequestsPaginated({
    category: "new" as ServiceCategoryEnum,
    limit: 1,
    enabled: requestType === "ordered",
  });

  const { data: orderedHistoryRequests, isLoading: isOrderedHistoryLoading } = useOrderedRequestsPaginated({
    category: "history" as ServiceCategoryEnum,
    limit: 1,
    enabled: requestType === "ordered",
  });

  const { data: orderedContracts, isLoading: isOrderedContractsLoading } = useOrderedContractsPaginated({
    category: "contract" as ServiceCategoryEnum,
    limit: 1,
    enabled: requestType === "ordered" && !!id,
    userId: String(id),
  });

  const isLoading =
    isProfileLoading ||
    (requestType === "received" && (isReceivedNewLoading || isReceivedHistoryLoading || isReceivedContractsLoading)) ||
    (requestType === "ordered" && (isOrderedNewLoading || isOrderedHistoryLoading || isOrderedContractsLoading));

  // Only count based on current request type
  const newCount =
    requestType === "received"
      ? receivedNewRequests?.meta.totalItems || 0
      : orderedNewRequests?.data.meta.totalItems || 0;

  const historyCount =
    requestType === "received"
      ? receivedHistoryRequests?.meta.totalItems || 0
      : orderedHistoryRequests?.data.meta.totalItems || 0;

  const contractCount =
    requestType === "received"
      ? receivedContracts?.data.meta.totalItems || 0
      : orderedContracts?.data.meta.totalItems || 0;

  return {
    newCount,
    contractCount,
    historyCount,
    isLoading,
  };
};
